import styled, { css } from "styled-components";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { revealStyle, revealDelayStyle } from "../styles/scrollReveal";
import { SectionTitle } from "./common/SectionTitle";
import TabletFieldBg from "../../../assets/images/pluvaIonLanding/feature01_tablet_field.jpg";
import DisplayTitleImg from "../../../assets/images/pluvaIonLanding/feature01_title_display.jpg";
import TabletAppImg from "../../../assets/images/pluvaIonLanding/feature02_tablet_app.png";
import TractorFieldImg from "../../../assets/images/pluvaIonLanding/feature02_tractor_field.jpg";
import PrecisionTitleImg from "../../../assets/images/pluvaIonLanding/feature02_title_precision.jpg";
import AiTractorImg from "../../../assets/images/pluvaIonLanding/feature03_ai_tractor.jpg";
import IntuitiveTitleImg from "../../../assets/images/pluvaIonLanding/feature03_title_intuitive.jpg";
import FieldExpansionImg from "../../../assets/images/pluvaIonLanding/feature04_field_expansion.jpg";
import ExpansionTitleImg from "../../../assets/images/pluvaIonLanding/feature04_title_expansion.jpg";

const descTextStyle = css`
  color: #c3d7e2;
  text-align: center;
  white-space: pre-line;

  font-family: Pretendard;
  font-size: 16px;
  font-weight: 600;
  line-height: 130%;
  letter-spacing: 0px;
`;

const RevealBlock = ({ children }: { children: React.ReactNode }) => {
  const reveal = useScrollReveal(0.4);
  return (
    <RevealWrapper ref={reveal.ref} $visible={reveal.isVisible}>
      {children}
    </RevealWrapper>
  );
};

const FeatureBlock = ({
  number,
  titleImg,
  titleAlt,
  subtitle,
}: {
  number: string;
  titleImg: string;
  titleAlt: string;
  subtitle: string | string[];
}) => {
  const reveal = useScrollReveal(0.2);
  const subtitles = Array.isArray(subtitle) ? subtitle : [subtitle];

  return (
    <FeatureItem ref={reveal.ref}>
      <FeatureNumber $visible={reveal.isVisible} $delay={0}>
        {number}
      </FeatureNumber>
      <Divider $visible={reveal.isVisible} $delay={200} />
      <FeatureTitleImage
        src={titleImg}
        alt={titleAlt}
        loading="lazy"
        $visible={reveal.isVisible}
        $delay={400}
      />
      {subtitles.map((text, index) => (
        <FeatureSubtitle
          key={index}
          $visible={reveal.isVisible}
          $delay={600 + index * 200}
        >
          {text}
        </FeatureSubtitle>
      ))}
    </FeatureItem>
  );
};

export const InnovationsSection = () => {
  const sectionTitleReveal = useScrollReveal(0.3);

  return (
    <Container>
      <SectionTitle
        ref={sectionTitleReveal.ref}
        $visible={sectionTitleReveal.isVisible}
      >
        {"차세대를 정의하는\n4가지 혁신"}
      </SectionTitle>

      {/* Feature 01 */}
      <FeatureBlock
        number="01"
        titleImg={DisplayTitleImg}
        titleAlt="압도적 시야"
        subtitle="크게, 밝게, 한눈에."
      />
      <RevealBlock>
        <FeatureImageSection>
          <FullWidthImage
            src={TabletFieldBg}
            alt="10.1인치 디스플레이"
            loading="lazy"
          />
          <OverlayDesc>
            {"눈이 편안한 10.1인치 디스플레이\n햇빛 아래서도 선명하게"}
          </OverlayDesc>
        </FeatureImageSection>
      </RevealBlock>

      {/* Feature 02 */}
      <FeatureBlock
        number="02"
        titleImg={PrecisionTitleImg}
        titleAlt="정밀 주행"
        subtitle="오차 없는 완벽한 줄맞춤"
      />
      <RevealBlock>
        <FeatureImageSection>
          <FullWidthImage
            src={TractorFieldImg}
            alt="±2.5cm 초정밀 주행"
            loading="lazy"
          />
          <TabletOverlay
            src={TabletAppImg}
            alt="플루바 아이온 주행 앱"
            loading="lazy"
          />
          <OverlayDesc>
            {"±2.5cm 초정밀 주행\n오메가·K·U턴 등 10개+ 자동 경로 주행"}
          </OverlayDesc>
        </FeatureImageSection>
      </RevealBlock>

      {/* Feature 03 */}
      <FeatureBlock
        number="03"
        titleImg={IntuitiveTitleImg}
        titleAlt="직관적인 작업"
        subtitle={["귀로 듣는 주행,", "더 완벽해진 작업 몰입도"]}
      />
      <RevealBlock>
        <FullWidthImage
          src={AiTractorImg}
          alt="플루바AI 실행 중"
          loading="lazy"
        />
        <BelowDesc>
          {"실시간 음성 안내\nAI 기반 필지 경계·헤드랜드 30초 자동 생성"}
        </BelowDesc>
      </RevealBlock>

      {/* Feature 04 */}
      <FeatureBlock
        number="04"
        titleImg={ExpansionTitleImg}
        titleAlt="무한한 확장성"
        subtitle="기계가 바뀌어도 감각은 그대로"
      />
      <RevealBlock>
        <FullWidthImage
          src={FieldExpansionImg}
          alt="트랙터부터 이앙기까지"
          loading="lazy"
        />
        <BelowDesc>
          {"확장 키트 하나로\n트랙터부터 이앙기까지 자유로운 이동"}
        </BelowDesc>
      </RevealBlock>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  padding: 56px 0 0;
  background-color: #000000;
`;

const RevealWrapper = styled.div<{ $visible: boolean }>`
  width: 100%;
  padding-bottom: 70px;

  ${revealStyle}
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  padding: 0 24px;
  margin-bottom: 72px;
`;

const FeatureNumber = styled.span<{
  $visible: boolean;
  $delay: number;
}>`
  align-self: center;

  font-family: Pretendard;
  font-size: 28px;
  font-weight: 700;
  color: #9ebdcc;
  letter-spacing: 2px;

  ${revealDelayStyle}
`;

const Divider = styled.div<{
  $visible: boolean;
  $delay: number;
}>`
  align-self: stretch;
  height: 1px;
  background-color: #9ebdcc;

  ${revealDelayStyle}
`;

const FeatureTitleImage = styled.img<{
  $visible: boolean;
  $delay: number;
}>`
  width: 100%;
  object-fit: contain;

  ${revealDelayStyle}
`;

const FeatureSubtitle = styled.p<{
  $visible: boolean;
  $delay: number;
}>`
  color: #ffffff;
  text-align: center;

  font-family: Pretendard;
  font-size: 20px;
  font-weight: 600;
  line-height: 130%;
  letter-spacing: -0.5px;

  &:not(:first-of-type) {
    margin-top: -12px;
  }

  ${revealDelayStyle}
`;

const FeatureImageSection = styled.div`
  position: relative;
  width: 100%;
`;

const FullWidthImage = styled.img`
  width: 100%;
  display: block;
  object-fit: cover;
`;

const TabletOverlay = styled.img`
  position: absolute;
  top: 5%;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  object-fit: contain;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.6));
`;

const OverlayDesc = styled.p`
  position: absolute;

  bottom: 10px;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 100%);

  ${descTextStyle}
`;

const BelowDesc = styled.p`
  /* padding: 0 56px; */

  ${descTextStyle}
`;
